import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigation } from "@refinedev/core";
import { ArrowLeft } from "@/components/ui/icon";

import type { ProcessGraphDTO } from "@aos/shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import {
  EntityGraph,
  type EntityGraphNode,
  type EntityGraphEdge,
} from "@/components/graph";
import { api } from "@/lib/api";

/**
 * Phase-role accent colours for the contour flow. These are graph node-type
 * **data** signals (design-system §4.6 — the one sanctioned exception to
 * black-only), low-saturation and confined to the node glyph; the in-flight
 * phase stays on the black accent.
 */
const PHASE_START = "#15803d"; // green — entry (low-saturation)
const PHASE_TERMINAL = "#64748b"; // slate — exit
const PHASE_MID = "#111111"; // near-black — in-flight phase (the black accent)

/**
 * Read-only process graph. Fetches `GET /api/processes/:id/graph` and renders
 * the phases/transitions through the shared {@link EntityGraph} (React Flow):
 * phases are nodes, transition rules are edges. `direction:"LR"` lets a phase
 * with multiple outgoing transitions branch into parallel lanes. Node/edge
 * lists from the DTO are summarized below the diagram. (The DTO's pre-rendered
 * `mermaid` field is no longer consumed here.)
 */
export function ProcessGraph() {
  const { id } = useParams<{ id: string }>();
  const { list } = useNavigation();

  const [graph, setGraph] = useState<ProcessGraphDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError("Missing process id.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ProcessGraphDTO>(`/processes/${id}/graph`)
      .then((dto) => {
        if (!cancelled) {
          setGraph(dto);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load process graph.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  // ----- EntityGraph adapter (B3): phases → nodes, transitions → edges. -----
  // direction "LR" + dagre fan-out makes a phase with multiple outgoing
  // transitions branch into parallel lanes (not a single column).
  const graphNodes = useMemo<EntityGraphNode[]>(
    () =>
      (graph?.nodes ?? []).map((n) => ({
        id: n.id,
        title: n.label || n.id,
        type: n.is_start ? "Start" : n.is_terminal ? "Terminal" : "Phase",
        subtitle: n.kind,
        tag: n.is_start ? "start" : n.is_terminal ? "end" : "phase",
        accent: n.is_start
          ? PHASE_START
          : n.is_terminal
            ? PHASE_TERMINAL
            : PHASE_MID,
      })),
    [graph],
  );

  const graphEdges = useMemo<EntityGraphEdge[]>(
    () =>
      (graph?.edges ?? []).map((e) => ({
        id: e.id,
        from: e.source,
        to: e.target,
        label: e.label,
        flow: e.rule_id,
      })),
    [graph],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Process Graph"
        subtitle="Phases as nodes and transition rules as edges — read-only."
        icon="processes"
        actions={
          <Button
            variant="outline"
            onClick={() => list("processes")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Flow</CardTitle>
          <CardDescription>
            Phases as nodes, transition rules as edges (read-only).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingState rows={5} />
          ) : error ? (
            <ErrorState message={error} onRetry={reload} />
          ) : graph && graph.nodes.length > 0 ? (
            <EntityGraph
              nodes={graphNodes}
              edges={graphEdges}
              layoutMode="layered"
              direction="LR"
              height="60vh"
              emptyMessage="No graph available."
            />
          ) : (
            <EmptyState
              icon="processes"
              title="No graph available"
              hint="This process has no phases or transitions yet."
            />
          )}
        </CardContent>
      </Card>

      {graph && !loading && !error ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Transitions</CardTitle>
          </CardHeader>
          <CardContent>
            {graph.edges.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No transitions defined.
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {graph.edges.map((edge) => {
                  const from = graph.nodes.find((n) => n.id === edge.source);
                  const to = graph.nodes.find((n) => n.id === edge.target);
                  return (
                    <li key={edge.id} className="flex items-center gap-2">
                      <span className="font-medium">
                        {from?.label ?? edge.source}
                      </span>
                      <span className="text-muted-foreground">
                        —{edge.label}→
                      </span>
                      <span className="font-medium">
                        {to?.label ?? edge.target}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({edge.rule_id})
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export default ProcessGraph;
